import difference from 'lodash/difference';
import {
  COMPLAINTS_STATUS,
  PROPERTY_DATA_FILTER_KEYS,
  PROPERTY_DATA_KV_MAP,
  VERIFICATION_STATUES,
  propertyActivityValues,
  propertyVisibilityValues,
} from '@/constants';
import { isPropertyIdentified } from '../utils/data-utils';

/**
 * Holds reusable/common parts of all functions for checkboxed filters
 *
 * @param {Immutable.Map} mapFilterParams Immutable object that holds the filter state
 * @param {string} propertyFieldValue Property field value to filter by
 * @param {string[]} filterKeys Keys in filter category
 * @param {string} filterCategory Key from mapFilterParams that holds filter values for a section
 * @returns true if allChecked, none checked, or if filter by value matches selected filter
 */
function checkShouldFilter({
  mapFilterParams,
  propertyFieldValue,
  filterKeys,
  filterCategory,
}) {
  const filterValues = filterKeys.map((key) =>
    mapFilterParams.getIn([filterCategory, key])
  );
  const everyTrue = filterValues.every((value) => value);
  const everyFalse = filterValues.every((value) => !value);
  const isPropertyAndFilterMatch = mapFilterParams.getIn([
    filterCategory,
    propertyFieldValue,
  ]);
  return everyTrue || everyFalse || isPropertyAndFilterMatch;
}

const testAnyResidentialTypeMatch = (mapFilterParams, residentialTypeValue) => {
  const isPrimary =
    residentialTypeValue === PROPERTY_DATA_KV_MAP.residentialType.primary;
  const isSecondaryInvestment =
    residentialTypeValue ===
    PROPERTY_DATA_KV_MAP.residentialType.secondaryInvestment;
  const isUnknown =
    residentialTypeValue === PROPERTY_DATA_KV_MAP.residentialType.unknown;

  const primaryChecked = mapFilterParams.getIn([
    'residentialType',
    PROPERTY_DATA_KV_MAP.residentialType.primary,
  ]);
  const secondaryInvestmentChecked = mapFilterParams.getIn([
    'residentialType',
    PROPERTY_DATA_KV_MAP.residentialType.secondaryInvestment,
  ]);
  const unknownChecked = mapFilterParams.getIn([
    'residentialType',
    PROPERTY_DATA_KV_MAP.residentialType.unknown,
  ]);

  if (!primaryChecked && !secondaryInvestmentChecked && !unknownChecked) {
    return true;
  }

  const isPrimaryMatch = primaryChecked && isPrimary;
  const isSecondaryInvestmentMatch =
    secondaryInvestmentChecked && isSecondaryInvestment;
  const isUnknownMatch = unknownChecked && isUnknown;

  // if all checkboxes are unchecked
  // there is no such a logic for this section

  return isPrimaryMatch || isSecondaryInvestmentMatch || isUnknownMatch;
};

/**
 *
 * @param {*} mapFilterParams
 * @param {*} property A property as given by the /properties API endpoint
 */
const testAddressIdentification = (mapFilterParams, property) => {
  // * the APN status that is selected by user, i.e., wanted by user
  const userWantsIdentified = mapFilterParams.getIn([
    'addressIdentification',
    PROPERTY_DATA_KV_MAP.addressIdentification.identifiedListing,
  ]);
  const userWantsUnidentified = mapFilterParams.getIn([
    'addressIdentification',
    PROPERTY_DATA_KV_MAP.addressIdentification.unidentifiedListing,
  ]);
  // if it's both, we show all properties, with or without apn
  if (
    (userWantsIdentified && userWantsUnidentified) ||
    (!userWantsIdentified && !userWantsUnidentified)
  ) {
    return true;
  }
  return (
    (isPropertyIdentified(property) && userWantsIdentified) ||
    (!isPropertyIdentified(property) && userWantsUnidentified)
  );
};

/**
 *
 * @param {*} mapFilterParams
 * @param {*} visibility values are { Live Now, Live Recently, Not Live }
 */
const testLiveListing = (mapFilterParams, visibility) => {
  const isLive = visibility === propertyVisibilityValues.liveNow;
  const notLive = visibility === propertyVisibilityValues.notLive;
  const isRecentLive = visibility === propertyVisibilityValues.liveRecently;

  // * the activeListing and inactiveListing status that is selected by user, i.e., wanted by user
  const userWantsLive = mapFilterParams.getIn([
    'visibility',
    PROPERTY_DATA_KV_MAP.visibility.liveNow,
  ]);
  const userWantsNonLive = mapFilterParams.getIn([
    'visibility',
    PROPERTY_DATA_KV_MAP.visibility.notLive,
  ]);
  const userWantsLiveRecently = mapFilterParams.getIn([
    'visibility',
    PROPERTY_DATA_KV_MAP.visibility.liveRecently,
  ]);

  if (
    (userWantsLive && userWantsNonLive) ||
    (!userWantsLive && !userWantsLiveRecently && !userWantsNonLive)
  ) {
    return true;
  }
  return (
    (userWantsNonLive && notLive) ||
    (userWantsLive && isLive) ||
    (userWantsLiveRecently && isRecentLive)
  );
};

/**
 *
 * @param {*} mapFilterParams
 * @param {*} str values are { true, false, 'ALL' }
 */
const testStrLicense = (mapFilterParams, str) => {
  // * the APN status that is selected by user, i.e., wanted by user
  const userWantsLicensed = mapFilterParams.getIn([
    'strLicense',
    PROPERTY_DATA_KV_MAP.strLicense.hasStrLicense,
  ]);
  const userWantsNoLicensed = mapFilterParams.getIn([
    'strLicense',
    PROPERTY_DATA_KV_MAP.strLicense.hasNoStrLicense,
  ]);
  // if it's all, we show all properties, with or without apn
  if (
    (userWantsLicensed && userWantsNoLicensed) ||
    (!userWantsLicensed && !userWantsNoLicensed)
  ) {
    return true;
  }

  return Boolean(str) === userWantsLicensed;
};

const testAnyPropertyLocation = (mapFilterParams, locationTypeValue) => {
  const isWithin =
    locationTypeValue === PROPERTY_DATA_KV_MAP.locationType.within;
  const isSelfReported =
    locationTypeValue === PROPERTY_DATA_KV_MAP.locationType.selfReported;
  const isNeighboring =
    locationTypeValue === PROPERTY_DATA_KV_MAP.locationType.neighboring;

  const withinChecked = mapFilterParams.getIn([
    'locationType',
    PROPERTY_DATA_KV_MAP.locationType.with,
  ]);
  const selfReportedChecked = mapFilterParams.getIn([
    'locationType',
    PROPERTY_DATA_KV_MAP.locationType.selfReported,
  ]);
  const neighboringChecked = mapFilterParams.getIn([
    'locationType',
    PROPERTY_DATA_KV_MAP.locationType.neighboring,
  ]);

  if (!withinChecked && !selfReportedChecked && !neighboringChecked) {
    return true;
  }

  const isWithinMatch = withinChecked && isWithin;
  const isSelfReportedMatch = selfReportedChecked && isSelfReported;
  const isNeighboringMatch = neighboringChecked && isNeighboring;

  return isWithinMatch || isSelfReportedMatch || isNeighboringMatch;
};

const testAnyUnitTypeMatch = (mapFilterParams, unitTypeValue) => {
  const isFullUnit =
    unitTypeValue === PROPERTY_DATA_KV_MAP.unitType.selfContained;
  const isPartial = unitTypeValue === PROPERTY_DATA_KV_MAP.unitType.room;
  const isUnknown = unitTypeValue === PROPERTY_DATA_KV_MAP.unitType.unknown;
  const hasOther =
    difference(
      [unitTypeValue],
      [
        PROPERTY_DATA_KV_MAP.unitType.selfContained,
        PROPERTY_DATA_KV_MAP.unitType.room,
        PROPERTY_DATA_KV_MAP.unitType.unknown,
      ]
    ).length > 0;

  const fullUnitChecked = mapFilterParams.getIn([
    'unitType',
    PROPERTY_DATA_KV_MAP.unitType.selfContained,
  ]);
  const partialChecked = mapFilterParams.getIn([
    'unitType',
    PROPERTY_DATA_KV_MAP.unitType.room,
  ]);
  const unknownChecked = mapFilterParams.getIn([
    'unitType',
    PROPERTY_DATA_KV_MAP.unitType.unknown,
  ]);
  const otherChecked = mapFilterParams.getIn([
    'unitType',
    PROPERTY_DATA_KV_MAP.unitType.other,
  ]);

  const isFullUnitMatch = fullUnitChecked && isFullUnit;
  const isPartialMatch = partialChecked && isPartial;
  const isUnknownMatch = unknownChecked && isUnknown;
  const isOtherMatch = otherChecked && hasOther;

  // if all are unchecked
  if (!fullUnitChecked && !partialChecked && !unknownChecked && !otherChecked) {
    return true;
  }

  return isFullUnitMatch || isPartialMatch || isUnknownMatch || isOtherMatch;
};

const testAnyRentalStructureMatch = (mapFilterParams, rentalStructureValue) => {
  const isMainStrucuture =
    rentalStructureValue === PROPERTY_DATA_KV_MAP.rentalStructure.mainStructure;
  const isAdu =
    rentalStructureValue === PROPERTY_DATA_KV_MAP.rentalStructure.adu;
  const isMultifamily =
    rentalStructureValue === PROPERTY_DATA_KV_MAP.rentalStructure.multiFamily;
  const isUnknown =
    rentalStructureValue === PROPERTY_DATA_KV_MAP.rentalStructure.unknown;

  const mainStructureChecked = mapFilterParams.getIn([
    'rentalStructure',
    PROPERTY_DATA_KV_MAP.rentalStructure.mainStructure,
  ]);
  const aduChecked = mapFilterParams.getIn([
    'rentalStructure',
    PROPERTY_DATA_KV_MAP.rentalStructure.adu,
  ]);
  const multiFamilyChecked = mapFilterParams.getIn([
    'rentalStructure',
    PROPERTY_DATA_KV_MAP.rentalStructure.multiFamily,
  ]);
  const unknownChecked = mapFilterParams.getIn([
    'rentalStructure',
    PROPERTY_DATA_KV_MAP.rentalStructure.unknown,
  ]);

  const isMainStrucutureMatch = mainStructureChecked && isMainStrucuture;
  const isAduMatch = aduChecked && isAdu;
  const isMultiFamilyMatch = multiFamilyChecked && isMultifamily;
  const isUnknownMatch = unknownChecked && isUnknown;

  if (
    !mainStructureChecked &&
    !aduChecked &&
    !multiFamilyChecked &&
    !unknownChecked
  ) {
    return true; // if all are unchecked
  }

  return (
    isMainStrucutureMatch || isAduMatch || isMultiFamilyMatch || isUnknownMatch
  );
};

const testOccupancyMatch = (mapFilterParams, occupancyValue) => {
  const minOccupancyValue = mapFilterParams.getIn(['occupancy', 'numberValue']);
  const includeUnknownOccupancy = mapFilterParams.getIn([
    'occupancy',
    'withUnknown',
  ]);
  // Ticket: RENTSCAPE-298, occupancy could be null, but in the map filter,
  // we call it "unknown occupancy"
  let occupancyMatch = true;

  // show the property/dot on map anyway
  // if there is not latestKpi or occupancy is null
  // but the popup card is empty
  // latestKpi could be object or undefined
  if (occupancyValue || occupancyValue === 0) {
    // for now, the value is a number or null
    occupancyMatch = occupancyValue >= minOccupancyValue;
  } else {
    occupancyMatch = includeUnknownOccupancy;
  }

  return occupancyMatch;
};

const testAnyOnlinePlatformMatch = (mapFilterParams, platforms) => {
  const platformOptions =
    mapFilterParams.getIn(['onlinePlatforms'])?.toJS() || {};
  const platformsToShow = Object.keys(platformOptions).filter((platformId) =>
    Boolean(platformOptions[platformId])
  );

  if (!platformsToShow.length) {
    return true;
  }

  const hasNone =
    platforms.length === 0 || platforms.includes(null) || platforms === null;

  const hasMatchingPlatforms = platforms.some((p) =>
    platformsToShow.includes(p)
  );

  const noneChecked = platformsToShow.includes('none');

  return (noneChecked && hasNone) || hasMatchingPlatforms;
};

function filterByHoaRegion(mapFilterParams, hoaRegions, hoaFilterSet) {
  if (hoaFilterSet.size === 0) return true; // If no filters, allow everything
  if (!hoaRegions) return false;
  return hoaRegions?.some((hoaName) => hoaFilterSet.has(hoaName.toString()));
}

function testRentalType(mapFilterParams, propertyRentalType, rentalTypes) {
  const rentalTypeKeys = Object.keys(rentalTypes);
  return checkShouldFilter({
    filterCategory: 'rentalType', // TODO: Use constants
    mapFilterParams,
    propertyFieldValue: propertyRentalType,
    filterKeys: rentalTypeKeys,
  });
}

function testViolationsMatched(mapFilterParams, propertyData) {
  const isHasViolationsChecked = mapFilterParams.getIn([
    'violations',
    'hasViolations',
  ]);
  const isHasNoViolationsChecked = mapFilterParams.getIn([
    'violations',
    'hasNoViolations',
  ]);
  const searchForViolationTypes =
    mapFilterParams.getIn(['violations', 'violationTypes']) || [];

  // When no violations are filtered, show all properties
  if (!isHasViolationsChecked && !isHasNoViolationsChecked) return true;

  const propertyViolations = propertyData?.violations || [];

  const violationsMatchingType = !searchForViolationTypes.length
    ? propertyViolations
    : propertyViolations.filter((v) => searchForViolationTypes.includes(v));

  const numViolations = violationsMatchingType?.length ?? 0;

  return (
    (isHasViolationsChecked && numViolations > 0) ||
    (isHasNoViolationsChecked && numViolations === 0)
  );
}

function testComplaintsRaised(mapFilterParams, complaints, propertyData) {
  const isHasComplaintsChecked = mapFilterParams.getIn([
    'complaints',
    'hasComplaints',
  ]);
  const isHasNoComplaintsChecked = mapFilterParams.getIn([
    'complaints',
    'hasNoComplaints',
  ]);
  const searchForComplaintTypes =
    mapFilterParams.getIn(['complaints', 'complaintTypes']) || [];

  // When no complaints are filtered, show all properties
  if (!isHasComplaintsChecked && !isHasNoComplaintsChecked) return true;

  const openComplaints = complaints.filter(
    (c) => c.status === COMPLAINTS_STATUS.OPEN
  );

  const complaintsMatchingType = !searchForComplaintTypes.length
    ? openComplaints
    : openComplaints.filter((c) =>
        c.complaintTypes?.some((t) => searchForComplaintTypes.includes(t))
      );

  const numComplaints = complaintsMatchingType?.length ?? 0;

  return (
    (isHasComplaintsChecked && numComplaints > 0) ||
    (isHasNoComplaintsChecked && numComplaints === 0)
  );
}

/**
 *
 * @param {*} mapFilterParams
 * @param {*} activity values are { Has Bookings, Has Availability, Blocked}
 */
function testActivity(mapFilterParams, activity) {
  const hasBookingsChecked = mapFilterParams.getIn([
    'activity',
    PROPERTY_DATA_KV_MAP.activity.hasBookings,
  ]);
  const hasAvailabilityChecked = mapFilterParams.getIn([
    'activity',
    PROPERTY_DATA_KV_MAP.activity.hasAvailability,
  ]);
  const isBlockedChecked = mapFilterParams.getIn([
    'activity',
    PROPERTY_DATA_KV_MAP.activity.isBlocked,
  ]);

  const hasBookings = activity === propertyActivityValues.hasBookings;
  const hasAvailability = activity === propertyActivityValues.hasAvailability;
  const isBlocked = activity === propertyActivityValues.isBlocked;

  if (!hasBookingsChecked && !hasAvailabilityChecked && !isBlockedChecked) {
    return true;
  }

  return (
    (hasBookings === true && hasBookingsChecked) ||
    (hasAvailability === true && hasAvailabilityChecked) ||
    (isBlocked === true && isBlockedChecked)
  );
}

function makeTestBooleanFlag(
  groupName,
  flagSetFilterName,
  flagUnsetFilterName,
  { treatNullAsFalse } = {}
) {
  return function testBooleanFlag(mapFilterParams, flagValue) {
    const userWantsFlagSet = mapFilterParams.getIn([
      groupName,
      flagSetFilterName,
    ]);
    const userWantsFlagUnset = mapFilterParams.getIn([
      groupName,
      flagUnsetFilterName,
    ]);

    if (!userWantsFlagSet && !userWantsFlagUnset) {
      return true;
    }

    const isFlagSet = Boolean(flagValue);
    const isFlagUnset = treatNullAsFalse ? !flagValue : flagValue === false;

    return (
      (userWantsFlagSet && isFlagSet) || (userWantsFlagUnset && isFlagUnset)
    );
  };
}

// const testVerified = makeTestBooleanFlag(
//   'verification',
//   PROPERTY_DATA_KV_MAP.verification.isVerified,
//   PROPERTY_DATA_KV_MAP.verification.isUnverified,
//   { treatNullAsFalse: true }
// );

/**
 *
 * @param {*} mapFilterParams
 * @param {*} verifiedStatus values are { "verified", "pending", "unverified" }
 */
const testVerifiedStatus = (mapFilterParams, verifiedStatus) => {
  const isVerified = verifiedStatus === VERIFICATION_STATUES.VERIFIED;
  const isPending =
    verifiedStatus === VERIFICATION_STATUES.PENDING_VERIFICATION;
  const isUnverified = verifiedStatus === VERIFICATION_STATUES.UNVERIFIED;

  const userWantsVerified = mapFilterParams.getIn([
    'verifiedStatus',
    PROPERTY_DATA_KV_MAP.verifiedStatus.isVerified,
  ]);

  const userWantsPending = mapFilterParams.getIn([
    'verifiedStatus',
    PROPERTY_DATA_KV_MAP.verifiedStatus.isPending,
  ]);

  const userWantsUnverified = mapFilterParams.getIn([
    'verifiedStatus',
    PROPERTY_DATA_KV_MAP.verifiedStatus.isUnverified,
  ]);

  const returnAllData =
    (userWantsVerified && userWantsPending && userWantsUnverified) ||
    (!userWantsVerified && !userWantsPending && !userWantsUnverified);

  if (returnAllData) return true;

  return (
    (userWantsVerified && isVerified) ||
    (userWantsPending && isPending) ||
    (userWantsUnverified && isUnverified)
  );
};

const testHOE = makeTestBooleanFlag(
  PROPERTY_DATA_FILTER_KEYS.hoe,
  PROPERTY_DATA_KV_MAP.hoe.claimingHoe,
  PROPERTY_DATA_KV_MAP.hoe.notClaimingHoe
);

const filterPropertyByCheckbox = ({
  propertyData,
  complaints,
  mapFilterParams,
  filterOpts,
  stats,
}) => {
  /** ------------address identification----------------- */
  const showPropertyWithApn = testAddressIdentification(
    mapFilterParams,
    propertyData
  );
  const showLiveProperty = testLiveListing(
    mapFilterParams,
    propertyData.visibility
  );
  /** ------------STR Licence ---------------------- */
  const showPropertyWithStrLicence = testStrLicense(
    mapFilterParams,
    propertyData.strLicense
  );
  /** ------------residential type section check----------------------- */
  const anyResidentialTypeCheck = testAnyResidentialTypeMatch(
    mapFilterParams,
    propertyData.residentialType
  );

  /** ---------------property location------------------- */
  const anyPropertyLocation = testAnyPropertyLocation(
    mapFilterParams,
    propertyData.locationType
  );

  /** ------------unit type check----------------------- */
  const anyUnitTypeCheck = testAnyUnitTypeMatch(
    mapFilterParams,
    propertyData.unitType
  );

  /** ------------rental structure check----------------------- */
  const anyRentalStructureCheck = testAnyRentalStructureMatch(
    mapFilterParams,
    propertyData.rentalStructure
  );

  /** ------------online platforms check----------------------- */
  const anyOnlinePlatformCheck = testAnyOnlinePlatformMatch(
    mapFilterParams,
    propertyData.onlinePlatforms
  );

  /** ------------complaints check----------------------- */
  const anyComplaintsRaised = testComplaintsRaised(
    mapFilterParams,
    complaints,
    propertyData
  );

  /** ------------violations check----------------------- */
  const anyViolationsMatched = testViolationsMatched(
    mapFilterParams,
    propertyData
  );

  /** ------------online platforms check----------------------- */
  const filterHoaCheck = filterByHoaRegion(
    mapFilterParams,
    propertyData.regions,
    filterOpts.hoaFilterSet
  );

  /** ------------rental Type check----------------------- */
  const filterRentalType = testRentalType(
    mapFilterParams,
    propertyData.propertyType,
    stats.rentalscape.rentalTypes
  );

  /** ------------activity check----------------------- */
  const filterActivity = testActivity(mapFilterParams, propertyData.activity);

  /** ------------check verified properties------------ */
  // const filterVerified = testVerified(mapFilterParams, propertyData.verified);
  const filterVerifiedStatus = testVerifiedStatus(
    mapFilterParams,
    propertyData.verifiedStatus
  );

  /** ------------check homeowner exemption------------ */
  const filterHOEVerified = testHOE(
    mapFilterParams,
    propertyData.ownerOccupied
  );

  return (
    showPropertyWithApn &&
    showLiveProperty &&
    showPropertyWithStrLicence &&
    anyResidentialTypeCheck &&
    anyPropertyLocation &&
    anyRentalStructureCheck &&
    anyUnitTypeCheck &&
    anyOnlinePlatformCheck &&
    anyComplaintsRaised &&
    anyViolationsMatched &&
    filterHoaCheck &&
    filterRentalType &&
    filterActivity &&
    filterHOEVerified &&
    filterVerifiedStatus
  );
};

export const propertyListFilter = ({
  propertyList,
  complaintsByProperty,
  mapFilterParams,
  stats,
}) => {
  const hoaFilterValues = mapFilterParams.get('hoa');
  const filterOpts = {
    hoaFilterSet: new Set(hoaFilterValues),
  };

  return propertyList.filter(
    (propertyData) =>
      testOccupancyMatch(mapFilterParams, propertyData.latestOccupancy) &&
      filterPropertyByCheckbox({
        propertyData,
        complaints: complaintsByProperty[propertyData.apn] || [],
        mapFilterParams,
        filterOpts,
        stats,
      })
  );
};
